import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientSettingsApi, type ClientSettings, type DefaultChecklistItem } from '../api/clientSettings';

export function useClientSettings(bookkeepingId: string | undefined) {
  return useQuery<ClientSettings>({
    queryKey: ['clientSettings', bookkeepingId],
    queryFn: () => clientSettingsApi.getSettings(bookkeepingId!),
    enabled: !!bookkeepingId,
  });
}

export function useDefaultChecklistItems(reviewType: 'quarterly' | 'year-end') {
  return useQuery<DefaultChecklistItem[]>({
    queryKey: ['defaultChecklistItems', reviewType],
    queryFn: () => clientSettingsApi.getDefaultChecklistItems(reviewType),
    staleTime: Infinity, // Default items don't change
  });
}

export function useToggleDefaultItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookkeepingId, data }: {
      bookkeepingId: string;
      data: { reviewType: 'quarterly' | 'year-end'; itemTitle: string; isEnabled: boolean };
    }) => clientSettingsApi.toggleDefaultItem(bookkeepingId, data),
    onSuccess: (data) => {
      queryClient.setQueryData(['clientSettings', data.bookkeepingId], data);
    },
  });
}

export function useAddCustomItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookkeepingId, data }: {
      bookkeepingId: string;
      data: { reviewType: 'quarterly' | 'year-end'; category: string; title: string; description?: string };
    }) => clientSettingsApi.addCustomItem(bookkeepingId, data),
    onSuccess: (data) => {
      queryClient.setQueryData(['clientSettings', data.bookkeepingId], data);
    },
  });
}

export function useDeleteCustomItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookkeepingId, itemId, reviewType }: {
      bookkeepingId: string;
      itemId: string;
      reviewType: 'quarterly' | 'year-end';
    }) => clientSettingsApi.deleteCustomItem(bookkeepingId, itemId, reviewType),
    onSuccess: (data) => {
      queryClient.setQueryData(['clientSettings', data.bookkeepingId], data);
    },
  });
}
